// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Sidebar.nav.css';
import * as styles from '../styles/Sidebar.styles';
import AccellorLogo from '../assets/images/accellorAILabs_logo_s.svg';
import icHome from '../assets/images/ic_Home.svg';
import icPeople from '../assets/images/ic_people.svg';
import icPerformance from '../assets/images/ic_performance.svg';
import icToken from '../assets/images/ic_tokens.svg';
import icPhone from '../assets/images/ic_phone.svg';
import icFeedback from '../assets/images/ic_feedback.svg';
import icReport from '../assets/images/ic_report.svg';

type SvgImport = string | React.ComponentType<React.SVGProps<SVGSVGElement>>;

const SvgOrImg = ({
  src,
  alt = '',
  width,
  height,
  ...props
}: {
  src: SvgImport;
  alt?: string;
  width?: number;
  height?: number;
} & React.SVGProps<SVGSVGElement>): JSX.Element =>
  typeof src === 'string' ? (
    <img src={src} alt={alt} width={width} height={height} />
  ) : (
    (() => {
      const Icon = src;
      return <Icon width={width} height={height} {...props} />;
    })()
  );

export interface SidebarProps {
  userInitial?: string;
  activeNav?: string;
  user?: { name?: string; username?: string };
  onLogout?: () => void;
}

const CloseXIcon = (): JSX.Element => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const Sidebar = ({ user, userInitial, activeNav, onLogout }: SidebarProps): JSX.Element => {
  const navigate = useNavigate();
  const reportsActive = activeNav === 'reports';
  // Calculate userInitial from user object if not provided
  const initial =
    userInitial ||
    (user?.name?.trim()
      ? user.name.trim().charAt(0).toUpperCase()
      : user?.username?.trim()
        ? user.username.trim().charAt(0).toUpperCase()
        : 'A');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [contactPopoverOpen, setContactPopoverOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (avatarRef.current && !avatarRef.current.contains(event.target as Node)) {
        setPopoverOpen(false);
      }
    };
    if (popoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popoverOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (contactRef.current && !contactRef.current.contains(event.target as Node)) {
        setContactPopoverOpen(false);
      }
    };
    if (contactPopoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contactPopoverOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setPopoverOpen(false);
        setContactPopoverOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleLogout = (): void => {
    setPopoverOpen(false);
    onLogout?.();
  };

  const handleAvatarKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setPopoverOpen((open) => !open);
    }
  };

  const handleLogoutKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleLogout();
    }
  };

  return (
    <aside
      style={{
        ...styles.sidebarRoot,
        ...(contactPopoverOpen ? styles.sidebarRootPopoverOpen : {})
      }}
    >
      <div style={styles.logoWrapper}>
        <SvgOrImg src={AccellorLogo} alt="Accellor Logo" width={40} height={40} />
      </div>
      <nav style={styles.navList}>
        <button
          style={reportsActive ? styles.navItemBase : styles.navItemActive}
          className={`sidebar-nav-btn${reportsActive ? '' : ' sidebar-nav-btn-active'}`}
          title="Home"
          type="button"
          onClick={() => navigate('/')}
        >
          <SvgOrImg src={icHome} alt="Home" width={22} height={22} aria-hidden />
        </button>
        <button
          style={reportsActive ? styles.navItemActive : styles.navItemBase}
          className={`sidebar-nav-btn${reportsActive ? ' sidebar-nav-btn-active' : ''}`}
          title="Reports"
          type="button"
          onClick={() => navigate('/reports')}
          aria-label="Open Reports"
        >
          <SvgOrImg src={icReport} alt="Reports" width={22} height={22} aria-hidden />
        </button>
        <a
          href="https://accellor-ai-labs.accellor.com/onboarding"
          target="_blank"
          rel="noopener noreferrer"
          style={styles.navItemBase}
          className="sidebar-nav-btn"
          title="Onboarding"
          aria-label="Open Onboarding in new tab"
        >
          <SvgOrImg src={icPeople} alt="Onboarding" width={22} height={22} aria-hidden />
        </a>
        <a
          href="https://accellor-ai-labs.accellor.com/performance"
          target="_blank"
          rel="noopener noreferrer"
          style={styles.navItemBase}
          className="sidebar-nav-btn"
          title="Performance"
          aria-label="Open Performance in new tab"
        >
          <SvgOrImg src={icPerformance} alt="Performance" width={22} height={22} aria-hidden />
        </a>
        <a
          href="https://accellor-ai-labs.accellor.com/tokenconsumption"
          target="_blank"
          rel="noopener noreferrer"
          style={styles.navItemBase}
          className="sidebar-nav-btn"
          title="Token consumption"
          aria-label="Open Token consumption in new tab"
        >
          <SvgOrImg src={icToken} alt="Token consumption" width={22} height={22} aria-hidden />
        </a>
        <a
          href="https://accellor-ai-labs.accellor.com/feedback"
          target="_blank"
          rel="noopener noreferrer"
          style={styles.navItemBase}
          className="sidebar-nav-btn"
          title="Feedback"
          aria-label="Open Feedback in new tab"
        >
          <SvgOrImg src={icFeedback} alt="Feedback" width={22} height={22} aria-hidden />
        </a>
        <div style={styles.contactPopoverWrapper} ref={contactRef}>
          <button
            style={styles.navItemBase}
            className="sidebar-nav-btn"
            title="Contact"
            type="button"
            onClick={() => setContactPopoverOpen((open) => !open)}
            aria-expanded={contactPopoverOpen}
            aria-haspopup="dialog"
            aria-label="Contact for queries"
          >
            <SvgOrImg src={icPhone} alt="Contact" width={22} height={22} aria-hidden />
          </button>
          {contactPopoverOpen && (
            <div role="dialog" aria-labelledby="contact-popover-title" style={styles.contactPopover}>
              <button
                type="button"
                onClick={() => setContactPopoverOpen(false)}
                style={styles.contactPopoverClose}
                aria-label="Close contact popover"
              >
                <CloseXIcon />
              </button>
              <h2 id="contact-popover-title" style={styles.contactPopoverTitle}>
                Please contact below for any queries:
              </h2>
              <p style={styles.contactPopoverLine}>
                Vaibhav Sharma –{' '}
                <a href="mailto:vaibhav.sharma@accellor.com" style={styles.contactPopoverMailto}>
                  vaibhav.sharma@accellor.com
                </a>
              </p>
              <p style={{ ...styles.contactPopoverLine, marginBottom: 0 }}>
                Ranjan Tatke –{' '}
                <a href="mailto:ranjan.tatke@accellor.com" style={styles.contactPopoverMailto}>
                  ranjan.tatke@accellor.com
                </a>
              </p>
            </div>
          )}
        </div>
      </nav>
      <div style={styles.avatarWrapper} ref={avatarRef}>
        <button
          type="button"
          className="sidebar-avatar-btn"
          style={styles.avatarBottom}
          onClick={() => setPopoverOpen((open) => !open)}
          onKeyDown={handleAvatarKeyDown}
          aria-expanded={popoverOpen}
          aria-haspopup="menu"
          aria-label="User menu"
          title="User menu"
        >
          {initial}
        </button>
        {popoverOpen && (
          <div role="menu" style={styles.popover} aria-label="User menu">
            <button
              type="button"
              role="menuitem"
              className="sidebar-popover-item"
              style={styles.popoverItem}
              onClick={handleLogout}
              onKeyDown={handleLogoutKeyDown}
              tabIndex={0}
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};
